import axios from 'axios';
import type {
  ApiResponse,
  Store,
  Employee,
  Service,
  Appointment,
  DashboardData,
  StoreSchedule,
  EmployeeSchedule
} from '@/models/api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

// Instance Axios pour les requêtes publiques
export const publicApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Instance Axios pour les requêtes authentifiées
export const privateApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Instance Axios pour les requêtes admin
export const adminApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Intercepteur pour ajouter le token aux requêtes authentifiées
privateApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour ajouter le token aux requêtes admin
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Méthodes de l'API publique
export const publicApiMethods = {
  // Authentification
  login: (credentials: { email: string; password: string }) =>
    publicApi.post<ApiResponse<{ token: string }>>('/auth/login', credentials),

  // Magasins publics
  getPublicStores: () => 
    publicApi.get<Store[]>('/stores'),

  // Services publics
  getPublicServices: () => 
    publicApi.get<ApiResponse<Service[]>>('/services'),

  // Créneaux disponibles
  getPublicSlots: (storeId: string) => 
    publicApi.get<ApiResponse<string[]>>(`/stores/${storeId}/slots`),
  
  // Créneaux réservés
  getBookedSlots: (storeId: number, date: string) =>
    publicApi.get<ApiResponse<{ date: string; time: string }[]>>(`/stores/${storeId}/booked-slots`, {
      params: { date }
    }),

  // Création d'une réservation
  createBooking: (data: {
    storeId: number;
    date: string;
    time: string;
    service: string;
    customerInfo: {
      name: string;
      email: string;
      phone: string;
      comments?: string;
    };
  }) => publicApi.post<ApiResponse<Appointment>>('/booking', data)
};

// Méthodes de l'API authentifiée
export const privateApiMethods = {
  // Rendez-vous
  getAppointments: (params?: any) => 
    privateApi.get<ApiResponse<Appointment[]>>('/appointments', { params }),

  createAppointment: (data: Omit<Appointment, 'id'>) => 
    privateApi.post<ApiResponse<Appointment>>('/appointments', data),

  getAppointmentById: (id: string) => 
    privateApi.get<ApiResponse<Appointment>>(`/appointments/${id}`),
  
  updateAppointment: (id: string, data: Partial<Appointment>) => 
    privateApi.put<ApiResponse<Appointment>>(`/appointments/${id}`, data),
  
  deleteAppointment: (id: string) => 
    privateApi.delete<ApiResponse<void>>(`/appointments/${id}`)
};

// Méthodes de l'API admin
export const adminApiMethods = {
  // Stores
  getStores: () => adminApi.get<Store[]>('/stores'),
  getStore: (id: number) => adminApi.get<Store>(`/stores/${id}`),
  createStore: (data: Omit<Store, 'id' | 'createdAt' | 'updatedAt'>) => 
    adminApi.post<Store>('/stores', data),
  updateStore: (id: number, data: Partial<Omit<Store, 'id' | 'createdAt' | 'updatedAt'>>) => 
    adminApi.put<Store>(`/stores/${id}`, data),
  deleteStore: (id: number) => adminApi.delete(`/stores/${id}`),

  // Services
  getServices: () => adminApi.get<Service[]>('/services'),
  getService: (id: number) => adminApi.get<Service>(`/services/${id}`),
  createService: (data: Omit<Service, 'id' | 'createdAt' | 'updatedAt'>) => 
    adminApi.post<Service>('/services', data),
  updateService: (id: number, data: Partial<Omit<Service, 'id' | 'createdAt' | 'updatedAt'>>) => 
    adminApi.put<Service>(`/services/${id}`, data),
  deleteService: (id: number) => adminApi.delete(`/services/${id}`),

  // Dashboard
  getDashboardData: () => 
    adminApi.get<ApiResponse<DashboardData>>('/admin/dashboard'),
  
  getStoreSchedule: (id: string) => 
    adminApi.get<ApiResponse<StoreSchedule>>(`/stores/${id}/schedule`),
  
  updateStoreSchedule: (id: string, schedule: StoreSchedule) => 
    adminApi.put<ApiResponse<void>>(`/stores/${id}/schedule`, schedule),

  // Employés
  getEmployees: () => 
    adminApi.get<ApiResponse<Employee[]>>('/employees'),

  getEmployeeById: (id: string) => 
    adminApi.get<ApiResponse<Employee>>(`/employees/${id}`),

  createEmployee: (data: Omit<Employee, 'id'>) => 
    adminApi.post<ApiResponse<Employee>>('/employees', data),

  updateEmployee: (id: string, data: Partial<Employee>) => 
    adminApi.put<ApiResponse<Employee>>(`/employees/${id}`, data),

  deleteEmployee: (id: string) => 
    adminApi.delete<ApiResponse<void>>(`/employees/${id}`),

  getEmployeeSchedule: (id: string) => 
    adminApi.get<ApiResponse<EmployeeSchedule>>(`/employees/${id}/schedule`),

  updateEmployeeSchedule: (id: string, schedule: EmployeeSchedule) => 
    adminApi.put<ApiResponse<void>>(`/employees/${id}/schedule`, schedule)
};

export default {
  publicApi,
  privateApi,
  adminApi,
  ...publicApiMethods,
  ...privateApiMethods,
  ...adminApiMethods
}; 